import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VehicleContext = createContext(null);

export function VehicleProvider({ children }) {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const refresh = useCallback(async () => {
    if (!user) {
      setVehicles([]);
      setSelectedId(null);
      return;
    }
    setLoading(true);
    try {
      const [{ data: list }, { data: notes }] = await Promise.all([
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/notifications`).catch(() => ({ data: [] })),
      ]);
      setVehicles(list || []);
      setNotifications(notes || []);
      setSelectedId((prev) => {
        if (prev && list.some((v) => v.id === prev)) return prev;
        const primary = list.find((v) => v.is_primary);
        return primary?.id || list[0]?.id || null;
      });
    } catch {
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const selected = vehicles.find((v) => v.id === selectedId) || null;

  return (
    <VehicleContext.Provider
      value={{
        vehicles,
        selected,
        selectedId,
        setSelectedId,
        loading,
        refresh,
        notifications,
        setNotifications,
      }}
    >
      {children}
    </VehicleContext.Provider>
  );
}

export function useVehicles() {
  const ctx = useContext(VehicleContext);
  if (!ctx) throw new Error("useVehicles must be used within VehicleProvider");
  return ctx;
}
